import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AlertCircle, Euro, Info, Percent, Plus, Tag, X } from "lucide-react";
import toast from "react-hot-toast";
import {
  adminService,
  type ActivityPromoRead,
  type PromoMarginMode,
  type PromoPreviewItemOverride,
  type PromoPreviewRequest,
} from "@/services/adminService";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Label,
  Spinner,
  Switch,
  ToggleGroup,
  fieldControlClass,
} from "@/components/ui";
import { SectionCard } from "@/components/common";
import { toastApiError } from "@/lib/errorMessages";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import { cn } from "@/lib/utils";
import {
  formatMoney,
  formatMoneyInput,
  parseMoneyInputToCents,
} from "@/lib/money";
import type { Activity } from "@/types";
import {
  planPromoSave,
  runPromoSave,
} from "@/pages/activities/detail/activityPromoSave";

interface AdminActivityPromoSectionProps {
  activity: Activity;
}

type OverrideDraft = {
  mode: PromoMarginMode;
  value: string;
};

function promosQueryKey(activityId: string) {
  return ["admin", "activity-promos", activityId] as const;
}

function previewContextQueryKey(activityId: string) {
  return ["admin", "activity-promos", activityId, "preview-context"] as const;
}

function previewQueryKey(
  activityId: string,
  request: PromoPreviewRequest | null,
) {
  return ["admin", "activity-promos", activityId, "preview", request] as const;
}

// The admin types euros for absolute margin and percent for percentage margin;
// the API stores cents and basis points respectively — both hundredths of the
// typed unit, so the money input parser (IT/EU or US separators) fits both.
const API_SCALE = 100;
const MAX_PERCENTAGE_MARGIN = 100 * API_SCALE;

/** Typed margin → API value (cents or basis points), or `null` if invalid. */
function parseMarginInput(mode: PromoMarginMode, raw: string): number | null {
  const apiValue = parseMoneyInputToCents(raw);
  if (apiValue === null || apiValue <= 0) return null;
  if (mode === "PERCENTAGE" && apiValue > MAX_PERCENTAGE_MARGIN) return null;
  return apiValue;
}

/** API value (cents or basis points) → typed margin in the UI locale (e.g. `1,50`). */
function toInputMarginValue(apiValue: number): string {
  return formatMoneyInput(apiValue);
}

/** All override drafts in API form, or `null` if any of them is invalid. */
function parseOverrides(
  overrides: Record<string, OverrideDraft>,
): PromoPreviewItemOverride[] | null {
  const parsed: PromoPreviewItemOverride[] = [];
  for (const [activityItemId, draft] of Object.entries(overrides)) {
    const marginValue = parseMarginInput(draft.mode, draft.value);
    if (marginValue === null) return null;
    parsed.push({
      activity_item_id: activityItemId,
      margin_mode: draft.mode,
      margin_value: marginValue,
    });
  }
  return parsed;
}

function discountPercent(discountCents: number, originalCents: number): number {
  if (originalCents <= 0) return 0;
  return Math.round((discountCents / originalCents) * 100);
}

export function AdminActivityPromoSection({
  activity,
}: AdminActivityPromoSectionProps) {
  const { t, i18n } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const activityId = activity.id;

  const [marginMode, setMarginMode] = useState<PromoMarginMode>("ABSOLUTE");
  const [marginValue, setMarginValue] = useState("");
  const [isPromoActive, setIsPromoActive] = useState(true);
  const [overrides, setOverrides] = useState<Record<string, OverrideDraft>>({});

  const { data: promos } = useQuery({
    queryKey: promosQueryKey(activityId),
    queryFn: () => adminService.listActivityPromos(activityId),
  });

  const { data: previewContext, isLoading: previewContextLoading } = useQuery({
    queryKey: previewContextQueryKey(activityId),
    queryFn: () => adminService.getActivityPromoPreviewContext(activityId),
  });

  const activityPromo = useMemo(
    () => promos?.find((promo) => promo.activity_item_id === null),
    [promos],
  );

  const itemPromos = useMemo(
    () =>
      (promos ?? []).filter(
        (promo): promo is ActivityPromoRead & { activity_item_id: string } =>
          promo.activity_item_id !== null,
      ),
    [promos],
  );

  // Re-seed the form whenever the saved promos change. Query structural sharing
  // keeps `promos` (and so both memos) referentially stable across refetches
  // that return identical data, so this does not clobber in-progress edits.
  useEffect(() => {
    if (activityPromo) {
      setMarginMode(activityPromo.margin_mode);
      setMarginValue(toInputMarginValue(activityPromo.margin_value));
      setIsPromoActive(activityPromo.is_active);
    } else {
      setMarginMode("ABSOLUTE");
      setMarginValue("");
      setIsPromoActive(true);
    }

    setOverrides(
      Object.fromEntries(
        itemPromos.map((promo) => [
          promo.activity_item_id,
          {
            mode: promo.margin_mode,
            value: toInputMarginValue(promo.margin_value),
          },
        ]),
      ),
    );
  }, [activityPromo, itemPromos]);

  const apiMarginValue = parseMarginInput(marginMode, marginValue);
  const globalValid = apiMarginValue !== null;
  const apiOverrides = useMemo(() => parseOverrides(overrides), [overrides]);

  const previewRequest = useMemo(
    (): Required<PromoPreviewRequest> | null =>
      apiMarginValue !== null && apiOverrides !== null
        ? {
            margin_mode: marginMode,
            margin_value: apiMarginValue,
            item_overrides: apiOverrides,
          }
        : null,
    [apiMarginValue, apiOverrides, marginMode],
  );
  const deferredPreviewRequest = useDeferredValue(previewRequest);

  const hasItems = (previewContext?.items.length ?? 0) > 0;

  const {
    data: previewData,
    isFetching: previewLoading,
    isError: previewFailed,
  } = useQuery({
    queryKey: previewQueryKey(activityId, deferredPreviewRequest),
    queryFn:
      deferredPreviewRequest !== null && hasItems
        ? () =>
            adminService.previewActivityPromos(
              activityId,
              deferredPreviewRequest,
            )
        : skipToken,
    placeholderData: (previous) => previous,
  });

  const rows = useMemo(() => {
    if (!previewContext) return [];
    const previewByItemId = new Map(
      (previewData?.items ?? []).map((item) => [
        item.activity_item_id,
        item.preview,
      ]),
    );
    const collator = new Intl.Collator(i18n.language, { sensitivity: "base" });
    return previewContext.items
      .map((item) => ({
        ...item,
        name: resolveCatalogTranslation(
          item.name_translations,
          i18n.language,
          item.name,
        ),
      }))
      .sort((a, b) => collator.compare(a.name, b.name))
      .map((item) => {
        const preview = previewByItemId.get(item.id);
        return {
          id: item.id,
          name: item.name,
          price: formatMoney(item.price_cents),
          hasOverride: overrides[item.id] !== undefined,
          maxMargin: preview ? formatMoney(preview.max_margin_cents) : null,
          minDiscount: preview ? formatMoney(preview.min_discount_cents) : null,
          minDiscountPercent: preview
            ? discountPercent(preview.min_discount_cents, item.price_cents)
            : null,
          medianDiscount: preview
            ? formatMoney(preview.median_discount_cents)
            : null,
          medianDiscountPercent: preview
            ? discountPercent(preview.median_discount_cents, item.price_cents)
            : null,
          maxDiscount: preview ? formatMoney(preview.max_discount_cents) : null,
          maxDiscountPercent: preview
            ? discountPercent(preview.max_discount_cents, item.price_cents)
            : null,
        };
      });
  }, [previewContext, previewData, overrides, i18n.language]);

  const addOverride = (itemId: string) => {
    if (!globalValid) return;
    setOverrides((prev) => ({
      ...prev,
      [itemId]: { mode: marginMode, value: marginValue },
    }));
  };

  const removeOverride = (itemId: string) =>
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });

  const updateOverride = (itemId: string, patch: Partial<OverrideDraft>) =>
    setOverrides((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...patch },
    }));

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: promosQueryKey(activityId) }),
      queryClient.invalidateQueries({
        queryKey: previewContextQueryKey(activityId),
      }),
      queryClient.invalidateQueries({ queryKey: ["activity", activityId] }),
      queryClient.invalidateQueries({ queryKey: ["activities"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "activities"] }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "seller", activity.user_id, "activities"],
      }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: (promo: Required<PromoPreviewRequest>) =>
      runPromoSave(activityId, planPromoSave(promo, isPromoActive, itemPromos)),
    onSuccess: async () => {
      toast.success(t("detail.adminPromoToastSaved"));
      await invalidate();
    },
    onError: (err) => toastApiError(err, "updateActivity"),
  });

  const removeMutation = useMutation({
    mutationFn: (promoId: string) =>
      adminService.deleteActivityPromo(activityId, promoId),
    onSuccess: async () => {
      toast.success(t("detail.adminPromoToastRemoved"));
      await invalidate();
    },
    onError: (err) => toastApiError(err, "updateActivity"),
  });

  const overrideCount = Object.keys(overrides).length;
  const previewState: "invalid" | "error" | "loading" | "ready" =
    previewRequest === null
      ? "invalid"
      : previewFailed
        ? "error"
        : previewData === undefined
          ? "loading"
          : "ready";
  const showPreviewColumns = previewState === "ready";

  const statusBadge = !activityPromo ? (
    <Badge
      variant="default"
      size="sm"
    >
      {t("detail.adminPromoNotConfigured")}
    </Badge>
  ) : activityPromo.is_active ? (
    <Badge
      variant="success"
      size="sm"
      dot
    >
      {t("detail.adminPromoActive")}
    </Badge>
  ) : (
    <Badge
      variant="warning"
      size="sm"
      dot
    >
      {t("detail.adminPromoPaused")}
    </Badge>
  );

  return (
    <SectionCard
      title={t("detail.adminPromoTitle")}
      icon={Tag}
      description={t("detail.adminPromoHint")}
      badge={statusBadge}
      contentClassName="space-y-0"
      footer={
        <div className="border-surface-200 bg-surface-50 dark:bg-surface-100/40 flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
          {activityPromo ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => removeMutation.mutate(activityPromo.id)}
              isLoading={removeMutation.isPending}
            >
              {t("detail.adminPromoRemove")}
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (previewRequest !== null) saveMutation.mutate(previewRequest);
            }}
            isLoading={saveMutation.isPending}
            disabled={previewRequest === null}
          >
            {t("detail.adminPromoSave")}
          </Button>
        </div>
      }
    >
      <div className="border-surface-200 bg-surface-50 dark:bg-surface-100/40 grid gap-4 rounded-lg border p-4 lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,17rem)] lg:items-end lg:gap-6">
        <div className="lg:pb-2">
          <Switch
            checked={isPromoActive}
            onChange={(e) => setIsPromoActive(e.target.checked)}
            label={t("detail.adminPromoEnabled")}
          />
        </div>
        <div className="min-w-0">
          <Label
            as="span"
            spacing="field"
            className="block"
          >
            {t("detail.adminPromoMarginMode")}
          </Label>
          <ToggleGroup
            value={marginMode}
            onChange={setMarginMode}
            ariaLabel={t("detail.adminPromoMarginMode")}
            options={[
              {
                value: "ABSOLUTE",
                label: t("detail.adminPromoMarginModeAbsolute"),
                icon: Euro,
              },
              {
                value: "PERCENTAGE",
                label: t("detail.adminPromoMarginModePercentage"),
                icon: Percent,
              },
            ]}
          />
        </div>
        <Input
          label={
            marginMode === "ABSOLUTE"
              ? t("detail.adminPromoMarginValueAbsolute")
              : t("detail.adminPromoMarginValuePercentage")
          }
          value={marginValue}
          onChange={(e) => setMarginValue(e.target.value)}
          inputMode="decimal"
          placeholder={marginMode === "ABSOLUTE" ? "0,00" : "0"}
          className="tabular-nums"
        />
      </div>

      <div className="mt-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
          <div className="min-w-0">
            <h3 className="text-surface-900 flex flex-wrap items-center gap-2 text-sm font-semibold">
              {t("detail.adminPromoPreviewTitle")}
              {overrideCount > 0 ? (
                <Badge
                  variant="primary"
                  size="sm"
                >
                  {t("detail.adminPromoOverrideCount", {
                    count: overrideCount,
                  })}
                </Badge>
              ) : null}
            </h3>
            <p className="text-surface-500 mt-0.5 text-[13px]">
              {t("detail.adminPromoPreviewHint")}
            </p>
          </div>
          {previewLoading || previewContextLoading ? (
            <span className="text-surface-400 flex items-center gap-1.5 text-xs">
              <Spinner
                size="sm"
                variant="muted"
                loadingLabel={tCommon("loading")}
              />
              {t("detail.adminPromoPreviewLoading")}
            </span>
          ) : null}
        </div>

        {previewContextLoading ? null : !hasItems ? (
          <EmptyState
            icon={Tag}
            title={t("detail.adminPromoNoItems")}
            className="border-surface-200 rounded-lg border border-dashed py-8"
          />
        ) : (
          <div className="border-surface-200 overflow-hidden rounded-lg border">
            {previewState === "invalid" ? (
              <div className="border-surface-200 bg-primary-50/60 text-primary-800 flex items-start gap-2 border-b px-4 py-2.5 text-[13px]">
                <Info
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden
                />
                {t("detail.adminPromoPreviewEmpty")}
              </div>
            ) : previewState === "error" ? (
              <div
                role="alert"
                className="border-surface-200 bg-error-50/60 text-error-800 flex items-start gap-2 border-b px-4 py-2.5 text-[13px]"
              >
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden
                />
                {t("detail.adminPromoPreviewError")}
              </div>
            ) : null}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[36rem] text-sm">
                <thead>
                  <tr className="border-surface-200 bg-surface-50 text-surface-500 dark:bg-surface-100/40 border-b text-xs font-medium">
                    <th
                      scope="col"
                      className="px-4 py-2.5 text-left font-medium"
                    >
                      {t("detail.adminPromoItemColumn")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-2.5 text-right font-medium"
                    >
                      {t("detail.adminPromoPriceColumn")}
                    </th>
                    {showPreviewColumns ? (
                      <>
                        <th
                          scope="col"
                          className="px-3 py-2.5 text-right font-medium"
                        >
                          {t("detail.adminPromoMaxMargin")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2.5 text-right font-medium"
                        >
                          {t("detail.adminPromoMinDiscount")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2.5 text-right font-medium"
                        >
                          {t("detail.adminPromoMedianDiscount")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2.5 text-right font-medium"
                        >
                          {t("detail.adminPromoMaxDiscount")}
                        </th>
                      </>
                    ) : null}
                    <th
                      scope="col"
                      className="px-4 py-2.5 text-right font-medium"
                    >
                      {t("detail.adminPromoOverrideColumn")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-surface-100 divide-y">
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        "transition-colors",
                        row.hasOverride
                          ? "bg-primary-50/60"
                          : "hover:bg-surface-50 dark:hover:bg-surface-100/50",
                      )}
                    >
                      <td className="max-w-[18rem] px-4 py-2.5">
                        <span
                          className="text-surface-900 block truncate font-medium"
                          title={row.name}
                        >
                          {row.name}
                        </span>
                      </td>
                      <td className="text-surface-600 px-3 py-2.5 text-right whitespace-nowrap tabular-nums">
                        {row.price}
                      </td>
                      {showPreviewColumns ? (
                        <>
                          <td className="px-3 py-2.5">
                            <PreviewCell amount={row.maxMargin} />
                          </td>
                          <td className="px-3 py-2.5">
                            <PreviewCell
                              amount={row.minDiscount}
                              percent={row.minDiscountPercent}
                            />
                          </td>
                          <td className="px-3 py-2.5">
                            <PreviewCell
                              amount={row.medianDiscount}
                              percent={row.medianDiscountPercent}
                            />
                          </td>
                          <td className="px-3 py-2.5">
                            <PreviewCell
                              amount={row.maxDiscount}
                              percent={row.maxDiscountPercent}
                            />
                          </td>
                        </>
                      ) : null}
                      <td className="px-4 py-2 whitespace-nowrap">
                        <OverrideEditor
                          draft={overrides[row.id]}
                          addLabel={t("detail.adminPromoAddOverride")}
                          removeLabel={t("detail.adminPromoRemoveOverride")}
                          marginValueAbsoluteLabel={t(
                            "detail.adminPromoMarginValueAbsolute",
                          )}
                          marginValuePercentageLabel={t(
                            "detail.adminPromoMarginValuePercentage",
                          )}
                          canAdd={globalValid}
                          onAdd={() => addOverride(row.id)}
                          onRemove={() => removeOverride(row.id)}
                          onChange={(patch) => updateOverride(row.id, patch)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function OverrideEditor({
  draft,
  addLabel,
  removeLabel,
  marginValueAbsoluteLabel,
  marginValuePercentageLabel,
  canAdd,
  onAdd,
  onRemove,
  onChange,
}: {
  draft: OverrideDraft | undefined;
  addLabel: string;
  removeLabel: string;
  marginValueAbsoluteLabel: string;
  marginValuePercentageLabel: string;
  canAdd: boolean;
  onAdd: () => void;
  onRemove: () => void;
  onChange: (patch: Partial<OverrideDraft>) => void;
}) {
  if (!draft) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onAdd}
          disabled={!canAdd}
          className="border-surface-200 text-surface-600 hover:bg-surface-50 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus
            className="h-3.5 w-3.5"
            aria-hidden
          />
          {addLabel}
        </button>
      </div>
    );
  }

  const invalid = parseMarginInput(draft.mode, draft.value) === null;

  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="border-surface-200 inline-flex overflow-hidden rounded-md border">
        <button
          type="button"
          onClick={() => onChange({ mode: "ABSOLUTE" })}
          className={cn(
            "px-2 py-1",
            draft.mode === "ABSOLUTE"
              ? "bg-primary-100 text-primary-700"
              : "bg-surface-0 text-surface-500 hover:bg-surface-50",
          )}
          aria-pressed={draft.mode === "ABSOLUTE"}
        >
          <Euro
            className="h-3.5 w-3.5"
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={() => onChange({ mode: "PERCENTAGE" })}
          className={cn(
            "px-2 py-1",
            draft.mode === "PERCENTAGE"
              ? "bg-primary-100 text-primary-700"
              : "bg-surface-0 text-surface-500 hover:bg-surface-50",
          )}
          aria-pressed={draft.mode === "PERCENTAGE"}
        >
          <Percent
            className="h-3.5 w-3.5"
            aria-hidden
          />
        </button>
      </div>
      <input
        value={draft.value}
        onChange={(e) => onChange({ value: e.target.value })}
        inputMode="decimal"
        aria-label={
          draft.mode === "ABSOLUTE"
            ? marginValueAbsoluteLabel
            : marginValuePercentageLabel
        }
        aria-invalid={invalid}
        className={fieldControlClass({
          size: "sm",
          className: "h-7 w-16 px-2 text-right tabular-nums",
        })}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        title={removeLabel}
        className="text-surface-400 hover:bg-surface-100 hover:text-surface-700 rounded-md p-1"
      >
        <X
          className="h-3.5 w-3.5"
          aria-hidden
        />
      </button>
    </div>
  );
}

function PreviewCell({
  amount,
  percent,
}: {
  amount: string | null;
  percent?: number | null;
}) {
  if (amount === null) {
    return (
      <span
        className="text-surface-300 block text-right"
        aria-hidden
      >
        —
      </span>
    );
  }
  return (
    <span className="flex items-baseline justify-end gap-1.5 whitespace-nowrap tabular-nums">
      <span className="text-surface-900 font-medium">{amount}</span>
      {percent != null ? (
        <span className="text-surface-500 text-xs">{percent}%</span>
      ) : null}
    </span>
  );
}
